import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"

import Text from "components/Text/Text"
import { IApiLink } from "@/types"
import useAssetVersions from "../hooks/useAssetVersions"
import VersionCollapsible from "../VersionCollapsible"
import Reservations from "components/Reservations/Reservations"
import Asset from "models/Asset"
import toastMessages from "stores/toastMessages"

interface IReservationsWithVersionsProps {
    asset: Asset
    isMachine: boolean
    onDeleteReservation?(): void
    linkGetAssetVersions?: IApiLink
    currentAssetVersion?: number
    isCurrentVersion?: boolean
}

const ReservationsWithVersions: FC<IReservationsWithVersionsProps> = ({
    asset,
    isMachine,
    onDeleteReservation,
    linkGetAssetVersions,
    currentAssetVersion,
    isCurrentVersion = true,
}) => {
    const { t } = useTranslation()
    const { assetVersions } = useAssetVersions(linkGetAssetVersions, toastMessages.showSystemError)

    // Sort versions: current first, then by version number descending
    const sortedVersions = useMemo(() => {
        if (!assetVersions) return null
        return [...assetVersions].sort((a, b) => {
            if (a.isCurrentVersion) return -1
            if (b.isCurrentVersion) return 1
            return b.version - a.version
        })
    }, [assetVersions])

    // Find the version being viewed
    const viewedVersion = useMemo(() => {
        if (!assetVersions || currentAssetVersion === undefined) return null
        return assetVersions.find(v => v.version === currentAssetVersion)
    }, [assetVersions, currentAssetVersion])

    // If viewing a previous version, only show that version's content (no version dropdowns)
    if (!isCurrentVersion) {
        return (
            <Reservations
                asset={asset}
                isMachine={isMachine}
                onDeleteReservation={undefined}
                overrideAssetId={viewedVersion?.assetId}
                readOnly
            />
        )
    }

    // If no versions available or just one version, show the regular Reservations
    if (!linkGetAssetVersions || !sortedVersions || sortedVersions.length <= 1) {
        return (
            <Reservations
                asset={asset}
                isMachine={isMachine}
                onDeleteReservation={onDeleteReservation}
            />
        )
    }

    // Get previous versions (non-current)
    const previousVersions = sortedVersions.filter(v => !v.isCurrentVersion)

    return (
        <>
            {/* Current version content shown directly without dropdown */}
            <Reservations
                asset={asset}
                isMachine={isMachine}
                onDeleteReservation={onDeleteReservation}
            />

            {/* Previous versions shown as collapsible dropdowns */}
            {previousVersions.length > 0 && (
                <div className="mt-4">
                    <Text type="h4" className="mb-2 color-grey-600">{t("asset.previousVersions")}</Text>
                    {previousVersions.map((version) => (
                        <VersionCollapsible
                            key={version.assetId}
                            version={version}
                            defaultExpanded={false}
                        >
                            {(isExpanded) => isExpanded && (
                                <Reservations
                                    asset={asset}
                                    isMachine={isMachine}
                                    onDeleteReservation={undefined}
                                    overrideAssetId={version.assetId}
                                    readOnly
                                />
                            )}
                        </VersionCollapsible>
                    ))}
                </div>
            )}
        </>
    )
}

export default ReservationsWithVersions
